import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api, API, formatValue } from "@/lib/api";
import PublicShell from "@/components/PublicShell";
import DmeTag from "@/components/DmeTag";
import { Loader2, Share2 } from "lucide-react";
import MiniTradeCalc from "@/components/MiniTradeCalc";

const FMT_LABEL = { "1qb": "1QB", superflex: "SUPERFLEX", tep: "TE PREMIUM" };

export default function PublicTrade() {
  const { shareId } = useParams();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
    catch { toast.error("Copy the URL from your address bar."); }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/public/trades/${shareId}`).then(r => setSnap(r.data)).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [shareId]);

  const winText = snap ? (snap.winner === "side_a" ? "Side A wins" : snap.winner === "side_b" ? "Side B wins" : "Even trade") : "";
  const winColor = snap?.winner === "even" ? "text-[var(--cyan)]" : "text-[var(--accent)]";
  const fairColor = (f) => f >= 90 ? "text-emerald-400" : f >= 75 ? "text-amber-400" : "text-red-400";
  const title = snap ? `${winText} — Dynasty Trade Verdict` : "Dynasty Trade Verdict";

  const SideCol = ({ side, title: t, faded }) => (
    <div className="flex-1 min-w-0">
      <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono mb-1">{t}</div>
      <div className={`font-mono text-2xl font-bold mb-3 ${faded ? "text-white" : "text-[var(--accent)]"}`}>{formatValue(side?.value || 0)}</div>
      {(side?.players || []).map((p, i) => (
        <div key={i} className="flex justify-between gap-2 text-sm py-1.5 border-t border-[var(--border)] text-[var(--text-2)]">
          <span className="truncate">{p.name} <span className="text-[var(--text-3)] text-xs">{p.position}</span></span>
          <span className="font-mono text-[var(--text-3)]">{formatValue(p.value)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <PublicShell>
      <Helmet>
        <title>{`${title} | Dynasty Metrics`}</title>
        <meta name="description" content="A Dynasty Metrics trade verdict — fairness score and proprietary DME value differential for both sides." />
        <meta property="og:title" content={title} />
        <meta property="og:image" content={`${API}/og/trades/${shareId}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${API}/og/trades/${shareId}.png`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      {loading && <div className="card-bordered p-12 flex justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>}

      {!loading && notFound && (
        <div className="card-bordered p-12 text-center" data-testid="public-trade-notfound">
          <div className="font-display text-xl font-bold mb-2">Verdict not found</div>
          <p className="text-sm text-[var(--text-2)] mb-5">This shared link may have expired.</p>
          <Link to="/" className="btn-primary text-sm inline-block">Explore Dynasty Metrics</Link>
        </div>
      )}

      {!loading && snap && (
        <div className="space-y-6 fade-up" data-testid="public-trade">
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// AI TRADE VERDICT · {FMT_LABEL[snap.format] || snap.format?.toUpperCase()}</div>
              <h1 className={`font-display text-4xl font-black uppercase tracking-tight ${winColor}`} data-testid="public-trade-heading">{winText}</h1>
            </div>
            <button onClick={copyLink} data-testid="public-trade-share-btn" className="btn-secondary text-sm inline-flex items-center gap-1.5"><Share2 className="w-4 h-4" /> Copy link</button>
          </header>

          <div className="card-bordered p-6">
            <div className="flex flex-wrap justify-between gap-6 mb-6">
              <div>
                <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">FAIRNESS</div>
                <div className={`font-mono text-5xl font-semibold ${fairColor(snap.fairness)}`} data-testid="public-trade-fairness">{snap.fairness}%</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono inline-flex items-center gap-1.5 justify-end">VALUE DIFF <DmeTag /></div>
                <div className="font-mono text-3xl font-semibold">{snap.diff >= 0 ? "+" : ""}{formatValue(snap.diff)}</div>
              </div>
            </div>
            <div className="flex gap-6">
              <SideCol side={snap.side_a} title="SIDE A GIVES" faded={snap.winner !== "even" && snap.winner !== "side_a"} />
              <div className="w-px bg-[var(--border)]" />
              <SideCol side={snap.side_b} title="SIDE B GIVES" faded={snap.winner !== "even" && snap.winner !== "side_b"} />
            </div>
          </div>

          <div className="card-bordered p-6 text-center" data-testid="public-trade-cta">
            <div className="font-display text-xl font-bold mb-2">Settle your next trade in seconds</div>
            <p className="text-sm text-[var(--text-2)] max-w-md mx-auto mb-4">Free dynasty Trade Calculator with proprietary DME values, fairness scores and shareable verdict cards.</p>
            <Link to="/login?signup=1" className="btn-primary text-sm inline-block" data-testid="public-trade-cta-btn">Run your own trade — free</Link>
          </div>

          <MiniTradeCalc format={snap.format || "1qb"} />
        </div>
      )}
    </PublicShell>
  );
}
